import { QueryClient } from '@tanstack/react-query';

/**
 * Single QueryClient shared by the entire console-web app.
 *
 * Defaults tuned for tenant workflows:
 *   - `staleTime: 30_000` — balance + key lists tolerate 30s staleness
 *     before a background re-fetch. Mutations invalidate explicitly
 *     (see the per-hook invalidation map in `src/api/*.ts`), so the
 *     longer window only affects view-only screens.
 *   - `retry: false` — error envelopes already encode `retryable`;
 *     surfacing the first error is clearer than silently retrying
 *     transient 5xx. Tests depend on this — retries force every
 *     assertion to await multiple ticks.
 *   - `refetchOnWindowFocus: false` — tenants may tab between pages
 *     while reading a transaction; a focus-driven refetch would clobber
 *     in-flight mutation state.
 *
 * Exported as a factory so tests can mint a fresh instance per render
 * and avoid cross-test cache bleed.
 */
export function createConsoleQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        retry: false,
        refetchOnWindowFocus: false,
      },
      mutations: {
        retry: false,
      },
    },
  });
}
